// ============================================================================
// TENANT APPLICATION ASSIGNMENT FORM MODAL
// ============================================================================
// Multi-section assign / edit form for tenant ↔ application assignments.
// Follows Pattern B (internally-managed formData) per CLAUDE.md blueprint.

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useTenants, useApplications } from 'hooks/api';
import {
  useCreateTenantAppAssignment,
  useUpdateTenantAppAssignment,
  useAvailableApps,
} from 'hooks/api';
import type {
  TenantAppAssignment,
  CreateTenantApplicationRequest,
  UpdateTenantApplicationRequest,
  ID,
} from 'api/types';
import {
  SUBSCRIPTION_PLANS,
  BILLING_CYCLES,
} from 'api/types/tenantAppAssignments.types';

// ============================================================================
// PROPS
// ============================================================================

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: TenantAppAssignment | null;
  onClose: () => void;
  onSaved: () => void;
  /** Pre-select tenant when arriving from a tenant-specific entry point. */
  defaultTenantId?: ID;
}

// ============================================================================
// FORM STATE
// ============================================================================

interface FormData {
  tenantId: string;
  appId: string;
  appVersion: string;
  // Keycloak
  keycloakClientId: string;
  /** Write-only: never prefilled from the record; blank in edit = keep existing. */
  keycloakClientSecret: string;
  callbackUrl: string;
  logoutUrl: string;
  // Database
  dbProvider: string;
  dbName: string;
  dbHost: string;
  dbPort: string;
  dbUsername: string;
  dbPassword: string;
  connectionString: string;
  skipDbConfig: boolean;
  // Subscription
  subscriptionPlanName: string;
  billingCycle: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  billingEmail: string;
  // Trial
  isTrial: boolean;
  trialEndDate: string;
  // Limits
  maxUsers: string;
  maxStorageGb: string;
  apiRateLimit: string;
}

const INITIAL: FormData = {
  tenantId: '',
  appId: '',
  appVersion: '',
  keycloakClientId: '',
  keycloakClientSecret: '',
  callbackUrl: '',
  logoutUrl: '',
  dbProvider: 'postgresql',
  dbName: '',
  dbHost: '',
  dbPort: '5432',
  dbUsername: '',
  dbPassword: '',
  connectionString: '',
  skipDbConfig: false,
  subscriptionPlanName: 'Standard',
  billingCycle: 'monthly',
  subscriptionStartDate: '',
  subscriptionEndDate: '',
  billingEmail: '',
  isTrial: false,
  trialEndDate: '',
  maxUsers: '100',
  maxStorageGb: '10',
  apiRateLimit: '1000',
};

// ============================================================================
// COMPONENT
// ============================================================================

const TenantApplicationAssignmentFormModal: React.FC<Props> = ({
  isOpen, mode, record, onClose, onSaved, defaultTenantId,
}) => {
  const [formData, setFormData] = useState<FormData>(INITIAL);
  const initialSnapshotRef = useRef('');

  // Tracks whether the user has actually interacted with any form field since
  // the last open. We don't trust JSON-stringify diffing alone here because
  // SyutSelect / async option resolution can transiently alter form values
  // during initial mount, producing false-positive "dirty" states.
  const [userTyped, setUserTyped] = useState(false);

  // Wrapper around setFormData that flips the userTyped flag — used by every
  // user-driven onChange handler in the form below.
  const updateForm = useCallback(
    (updater: (prev: FormData) => FormData) => {
      setUserTyped(true);
      setFormData(updater);
    },
    []
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors((prev) => {
      if (!prev[f]) return prev;
      const next = { ...prev };
      delete next[f];
      return next;
    });
  }, []);

  // Confirmation + saving state
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Lookups
  const isEdit = mode === 'edit';
  const { data: tenants = [] } = useTenants({ pageNumber: 1, pageSize: 500 } as any);
  const { data: apps = [] } = useApplications();
  // Apps not yet assigned to the chosen tenant — drives the picker in BOTH modes
  // (edit additionally offers the currently-assigned app so its label resolves)
  const { data: availableApps = [] } = useAvailableApps(
    formData.tenantId ? formData.tenantId : null
  );

  // Mutations
  const { create } = useCreateTenantAppAssignment();
  const { update } = useUpdateTenantAppAssignment();

  // Populate form when opened
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    let next: FormData;
    if (record) {
      next = {
        tenantId: String(record.tenantId),
        appId: String(record.appId),
        appVersion: record.tenantAppVersion ?? record.appVersion ?? '',
        keycloakClientId: record.keycloakClientId ?? '',
        keycloakClientSecret: '', // never echoed back — blank means keep existing
        callbackUrl: record.callbackUrl ?? '',
        logoutUrl: record.logoutUrl ?? '',
        dbProvider: record.dbProvider ?? 'postgresql',
        dbName: record.dbName ?? '',
        dbHost: record.dbHost ?? '',
        dbPort: String(record.dbPort ?? 5432),
        dbUsername: record.dbUsername ?? '',
        dbPassword: '',
        connectionString: record.connectionString ?? '',
        skipDbConfig: !record.dbHost && !record.connectionString,
        subscriptionPlanName: record.subscriptionPlanName ?? 'Standard',
        billingCycle: record.billingCycle ?? 'monthly',
        subscriptionStartDate: record.subscriptionStartDate?.slice(0, 10) ?? '',
        subscriptionEndDate: record.subscriptionEndDate?.slice(0, 10) ?? '',
        billingEmail: record.billingEmail ?? '',
        isTrial: !!record.isTrial,
        trialEndDate: record.trialEndDate?.slice(0, 10) ?? '',
        maxUsers: String(record.maxUsers ?? 100),
        maxStorageGb: String(record.maxStorageGb ?? 10),
        apiRateLimit: String(record.apiRateLimit ?? 1000),
      };
    } else {
      next = { ...INITIAL, tenantId: defaultTenantId ? String(defaultTenantId) : '' };
    }
    setFormData(next);
    initialSnapshotRef.current = JSON.stringify(next);
    setFieldErrors({});
    setUserTyped(false);
  }, [isOpen]);

  // isDirty is true ONLY when the user has typed AND the current form data
  // actually diverges from the initial snapshot. This avoids false-positive
  // discard prompts on a freshly-opened, untouched form.
  const isDirty = useMemo(() => {
    if (!userTyped) return false;
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData, userTyped]);

  // Tenant + app dropdown options
  const tenantOptions = useMemo(
    () => (tenants ?? []).map((t: any) => ({
      value: String(t.tenantId ?? t.id),
      label: `${t.tenantCode ?? ''} — ${t.tenantName ?? t.name ?? ''}`,
    })),
    [tenants]
  );

  // Both modes list only apps NOT yet assigned to the selected tenant — an
  // already-assigned target is rejected by the backend. Edit mode prepends the
  // currently-assigned app so its label resolves and it stays selectable.
  const appOptions = useMemo(() => {
    const options = (availableApps ?? []).map((a: any) => ({
      value: String(a.appId ?? a.id),
      label: `${a.appCode ?? a.code ?? ''} — ${a.appName ?? a.name ?? ''}`,
    }));
    if (isEdit && record && !options.some((o) => o.value === String(record.appId))) {
      options.unshift({
        value: String(record.appId),
        label: `${record.appCode ?? ''} — ${record.appName ?? ''}`,
      });
    }
    return options;
  }, [isEdit, record, availableApps]);

  // Handlers
  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.tenantId) errors.tenantId = 'Tenant is required';
    if (!formData.appId) errors.appId = 'Application is required';
    if (!formData.subscriptionPlanName) errors.subscriptionPlanName = 'Plan is required';
    if (formData.isTrial && !formData.trialEndDate) errors.trialEndDate = 'Trial end date is required when trial is enabled';
    if (formData.subscriptionStartDate && formData.subscriptionEndDate
        && formData.subscriptionStartDate > formData.subscriptionEndDate) {
      errors.subscriptionEndDate = 'End date must be after start date';
    }
    const maxUsersN = Number(formData.maxUsers);
    if (Number.isNaN(maxUsersN) || maxUsersN < 1) errors.maxUsers = 'Must be a positive number';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      // Backend DTO requires appName/appCode ([Required]) — resolve them from
      // the selected application (create) or the existing record (edit).
      // Search both lists: create-mode options come from availableApps.
      const selectedApp = [...(availableApps ?? []), ...(apps ?? [])].find(
        (a: any) => String(a.appId ?? a.id) === String(formData.appId)
      );
      const payload: CreateTenantApplicationRequest | UpdateTenantApplicationRequest = {
        appName: selectedApp?.appName ?? record?.appName ?? '',
        appCode: selectedApp?.appCode ?? record?.appCode ?? '',
        appVersion: formData.appVersion || undefined,
        // Client ID is always sent in edit (backend treats empty = clear, null = keep);
        // secret only in edit and only when the user typed one (blank = keep existing).
        keycloakClientId: isEdit ? formData.keycloakClientId : formData.keycloakClientId || undefined,
        keycloakClientSecret: isEdit && formData.keycloakClientSecret ? formData.keycloakClientSecret : undefined,
        callbackUrl: formData.callbackUrl || undefined,
        logoutUrl: formData.logoutUrl || undefined,
        dbProvider: formData.skipDbConfig ? undefined : formData.dbProvider,
        dbName: formData.skipDbConfig ? undefined : formData.dbName || undefined,
        dbHost: formData.skipDbConfig ? undefined : formData.dbHost || undefined,
        dbPort: formData.skipDbConfig ? undefined : Number(formData.dbPort) || undefined,
        dbUsername: formData.skipDbConfig ? undefined : formData.dbUsername || undefined,
        dbPassword: formData.skipDbConfig || !formData.dbPassword ? undefined : formData.dbPassword,
        // Edit always sends the field (the form shows the stored value, so an
        // emptied box means "clear it"); create omits it when blank.
        connectionString: formData.skipDbConfig
          ? undefined
          : isEdit
            ? formData.connectionString
            : formData.connectionString || undefined,
        subscriptionPlanName: formData.subscriptionPlanName,
        billingCycle: formData.billingCycle,
        subscriptionStartDate: formData.subscriptionStartDate || undefined,
        subscriptionEndDate: formData.subscriptionEndDate || undefined,
        billingEmail: formData.billingEmail || undefined,
        isTrial: formData.isTrial,
        trialEndDate: formData.isTrial ? formData.trialEndDate : undefined,
        maxUsers: Number(formData.maxUsers),
        maxStorageGb: Number(formData.maxStorageGb),
        apiRateLimit: Number(formData.apiRateLimit),
      };

      if (mode === 'create') {
        await create(formData.tenantId, { ...payload, appId: formData.appId });
        notificationService.toast.success('Application assigned successfully.');
      } else if (record) {
        // Route uses the ORIGINAL appId to address the row; body carries the
        // (possibly different) selected appId to move the assignment.
        await update(record.tenantId, record.appId, { ...payload, appId: formData.appId });
        notificationService.toast.success('Assignment updated successfully.');
      }
      setIsSaving(false);
      onSaved();
    } catch (err: any) {
      setIsSaving(false);
      // Prefer the API's own message (e.g. "Application with this code already
      // exists") over axios' generic "Request failed with status code 400";
      // model-validation replies carry per-field messages under errors instead.
      const data = err?.response?.data;
      const fieldErrors = data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)
        ? Object.values(data.errors as Record<string, string[]>).flat().join('; ')
        : '';
      notificationService.toast.error(
        data?.message || fieldErrors || err?.message || 'An error occurred. Please try again.'
      );
    }
  }, [formData, mode, record, apps, availableApps, create, update, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {isEdit && record
              ? `Edit Assignment: ${record.tenantName} → ${record.appName}`
              : 'Assign Application to Tenant'}
          </h2>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* ── ASSIGNMENT SECTION ───────────────────────────────────── */}
          <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: 600 }}>Assignment</h4>
          <div className="form-grid-2">
            <div className="form-group" data-field="tenantId">
              <SyutSelect
                label="Tenant"
                required
                isDisabled={isEdit}
                value={formData.tenantId}
                onChange={(v) => { clearFieldError('tenantId'); updateForm((p) => ({ ...p, tenantId: (v as string) || '' })); }}
                options={tenantOptions}
                error={getFieldError('tenantId')}
              />
            </div>
            <div className="form-group" data-field="appId">
              <SyutSelect
                label="Application"
                required
                value={formData.appId}
                onChange={(v) => { clearFieldError('appId'); updateForm((p) => ({ ...p, appId: (v as string) || '' })); }}
                options={appOptions}
                error={getFieldError('appId')}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Version</label>
            <input
              type="text"
              className="form-input"
              value={formData.appVersion}
              onChange={(e) => updateForm((p) => ({ ...p, appVersion: e.target.value }))}
              placeholder="e.g. 1.0.0"
            />
          </div>

          {/* ── KEYCLOAK CLIENT SECTION ──────────────────────────────── */}
          <h4 style={{ margin: '1.25rem 0 0.75rem 0', fontSize: '0.875rem', fontWeight: 600 }}>Keycloak Client</h4>
          <div className={isEdit ? 'form-grid-2' : undefined}>
            <div className="form-group">
              <label className="form-label">Client ID</label>
              <input type="text" className="form-input"
                value={formData.keycloakClientId}
                onChange={(e) => updateForm((p) => ({ ...p, keycloakClientId: e.target.value }))}
                placeholder="auto-generated if blank" />
            </div>
            {/* Secret is editable only on an existing assignment; create keeps prior behavior */}
            {isEdit && (
              <div className="form-group">
                <label className="form-label">Client Secret <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>(blank = keep existing)</span></label>
                <input type="password" className="form-input"
                  autoComplete="new-password"
                  value={formData.keycloakClientSecret}
                  onChange={(e) => updateForm((p) => ({ ...p, keycloakClientSecret: e.target.value }))}
                  placeholder="••••••••" />
              </div>
            )}
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Callback URL</label>
              <input type="text" className="form-input"
                value={formData.callbackUrl}
                onChange={(e) => updateForm((p) => ({ ...p, callbackUrl: e.target.value }))}
                placeholder="https://app.example.com/auth/callback" />
            </div>
            <div className="form-group">
              <label className="form-label">Logout URL</label>
              <input type="text" className="form-input"
                value={formData.logoutUrl}
                onChange={(e) => updateForm((p) => ({ ...p, logoutUrl: e.target.value }))}
                placeholder="https://app.example.com/logout" />
            </div>
          </div>

          {/* ── DATABASE SECTION ─────────────────────────────────────── */}
          <h4 style={{ margin: '1.25rem 0 0.75rem 0', fontSize: '0.875rem', fontWeight: 600 }}>Database</h4>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
              <input type="checkbox" checked={formData.skipDbConfig}
                onChange={(e) => updateForm((p) => ({ ...p, skipDbConfig: e.target.checked }))} />
              Configure database later
            </label>
          </div>
          {!formData.skipDbConfig && (
            <>
              <div className="form-grid-3">
                <div className="form-group">
                  <SyutSelect
                    label="Provider"
                    options={[
                      { value: 'postgresql', label: 'PostgreSQL' },
                      { value: 'mysql', label: 'MySQL' },
                      { value: 'sqlserver', label: 'SQL Server' },
                      { value: 'oracle', label: 'Oracle' },
                    ]}
                    value={formData.dbProvider}
                    onChange={(v) => updateForm((p) => ({ ...p, dbProvider: (v as string) || 'postgresql' }))}
                    isClearable={false}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Host</label>
                  <input type="text" className="form-input"
                    value={formData.dbHost}
                    onChange={(e) => updateForm((p) => ({ ...p, dbHost: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Port</label>
                  <input type="number" className="form-input"
                    value={formData.dbPort}
                    onChange={(e) => updateForm((p) => ({ ...p, dbPort: e.target.value }))} />
                </div>
              </div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Database Name</label>
                  <input type="text" className="form-input"
                    value={formData.dbName}
                    onChange={(e) => updateForm((p) => ({ ...p, dbName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input type="text" className="form-input"
                    value={formData.dbUsername}
                    onChange={(e) => updateForm((p) => ({ ...p, dbUsername: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Password {isEdit && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>(blank = keep existing)</span>}</label>
                  <input type="password" className="form-input"
                    value={formData.dbPassword}
                    onChange={(e) => updateForm((p) => ({ ...p, dbPassword: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Connection String</label>
                <input type="text" className="form-input"
                  value={formData.connectionString}
                  onChange={(e) => updateForm((p) => ({ ...p, connectionString: e.target.value }))}
                  placeholder="Host=db.example.com;Port=5432;Database=tenant_db;Username=app_user;Password=..." />
              </div>
            </>
          )}

          {/* ── SUBSCRIPTION SECTION ─────────────────────────────────── */}
          <h4 style={{ margin: '1.25rem 0 0.75rem 0', fontSize: '0.875rem', fontWeight: 600 }}>Subscription</h4>
          <div className="form-grid-3">
            <div className="form-group" data-field="subscriptionPlanName">
              <SyutSelect
                label="Plan"
                required
                options={SUBSCRIPTION_PLANS.map((p) => ({ value: p, label: p }))}
                value={formData.subscriptionPlanName}
                onChange={(v) => { clearFieldError('subscriptionPlanName'); updateForm((p) => ({ ...p, subscriptionPlanName: (v as string) || '' })); }}
                error={getFieldError('subscriptionPlanName')}
                isClearable={false}
              />
            </div>
            <div className="form-group">
              <SyutSelect
                label="Billing Cycle"
                options={BILLING_CYCLES.map((c) => ({ value: c, label: c }))}
                value={formData.billingCycle}
                onChange={(v) => updateForm((p) => ({ ...p, billingCycle: (v as string) || 'monthly' }))}
                isClearable={false}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Billing Email</label>
              <input type="email" className="form-input"
                value={formData.billingEmail}
                onChange={(e) => updateForm((p) => ({ ...p, billingEmail: e.target.value }))} />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Subscription Start</label>
              <input type="date" className="form-input"
                value={formData.subscriptionStartDate}
                onChange={(e) => updateForm((p) => ({ ...p, subscriptionStartDate: e.target.value }))} />
            </div>
            <div className="form-group" data-field="subscriptionEndDate">
              <label className="form-label">Subscription End</label>
              <input type="date"
                className={`form-input${getFieldError('subscriptionEndDate') ? ' form-field-error' : ''}`}
                value={formData.subscriptionEndDate}
                onChange={(e) => { clearFieldError('subscriptionEndDate'); updateForm((p) => ({ ...p, subscriptionEndDate: e.target.value })); }} />
              {getFieldError('subscriptionEndDate') && <div className="form-error-message">{getFieldError('subscriptionEndDate')}</div>}
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginTop: '1.25rem' }}>
                <input type="checkbox" checked={formData.isTrial}
                  onChange={(e) => updateForm((p) => ({ ...p, isTrial: e.target.checked }))} />
                Trial period
              </label>
            </div>
            {formData.isTrial && (
              <div className="form-group" data-field="trialEndDate">
                <label className="form-label">Trial Ends <span className="text-danger">*</span></label>
                <input type="date"
                  className={`form-input${getFieldError('trialEndDate') ? ' form-field-error' : ''}`}
                  value={formData.trialEndDate}
                  onChange={(e) => { clearFieldError('trialEndDate'); updateForm((p) => ({ ...p, trialEndDate: e.target.value })); }} />
                {getFieldError('trialEndDate') && <div className="form-error-message">{getFieldError('trialEndDate')}</div>}
              </div>
            )}
          </div>

          {/* ── LIMITS SECTION ───────────────────────────────────────── */}
          <h4 style={{ margin: '1.25rem 0 0.75rem 0', fontSize: '0.875rem', fontWeight: 600 }}>Limits</h4>
          <div className="form-grid-3">
            <div className="form-group" data-field="maxUsers">
              <label className="form-label">Max Users <span className="text-danger">*</span></label>
              <input type="number"
                className={`form-input${getFieldError('maxUsers') ? ' form-field-error' : ''}`}
                value={formData.maxUsers}
                onChange={(e) => { clearFieldError('maxUsers'); updateForm((p) => ({ ...p, maxUsers: e.target.value })); }}
                min="1" />
              {getFieldError('maxUsers') && <div className="form-error-message">{getFieldError('maxUsers')}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Storage (GB)</label>
              <input type="number" className="form-input"
                value={formData.maxStorageGb}
                onChange={(e) => updateForm((p) => ({ ...p, maxStorageGb: e.target.value }))}
                min="0" />
            </div>
            <div className="form-group">
              <label className="form-label">API Rate Limit (req/min)</label>
              <input type="number" className="form-input"
                value={formData.apiRateLimit}
                onChange={(e) => updateForm((p) => ({ ...p, apiRateLimit: e.target.value }))}
                min="0" />
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {isEdit ? 'Save Changes' : 'Assign'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={isEdit ? 'Confirm Update' : 'Confirm Assign'}
        message={isEdit ? 'Save changes to this assignment?' : 'Assign this application to the selected tenant?'}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default TenantApplicationAssignmentFormModal;
